import React, {Dispatch, PropsWithChildren, SetStateAction, useEffect, useState} from 'react';
import InterestServiceClient, { useInterestService } from '../../sdk/InterestServiceClient';
import EodoServiceClient, { useEodoService } from '../../sdk/EodoServiceClient';
import LedgerServiceClient, { useLedgerService } from '../../sdk/LedgerServiceClient';
import TransactionServiceClient, { useTransactionService } from '../../sdk/TransactionServiceClient';
import RegularPaymentServiceClient, { useRegularPaymentService } from '../../sdk/RegularPaymentServiceClient';
import CustomerInformationSystemClient, {
  useCustomerInformationSystemService,
} from '../../sdk/CustomerInformationSystemClient';
import FeeServiceClient, { useFeeService } from '../../sdk/FeeServiceClient';
import SearchServiceClient, { useSearchService } from '../../sdk/SearchServiceClient';
import CreditAllocationServiceClient, { useCreditAllocationService } from '../../sdk/CreditAllocationServiceClient';
import FakeAccessPayClient, { useFakeAccessPayClient } from '../../sdk/FakeAccessPayClient';
import { useGlobalContext } from '../../GlobalContextProvider';
import { useAccountIdContext } from './AccountIdContext';
import {useAccessPayConfiguration} from "@app/sdk";

interface IAccountContext {
  error: string;
  setError: Dispatch<string>;
  billingDate: Date;
  setBillingDate: Dispatch<Date>;
  eodDate: Date;
  setEodDate: Dispatch<Date>;
  isRunningEod: boolean;
  setIsRunningEod: Dispatch<boolean>;
  previousBillingPeriod: BillingPeriod;
  setPreviousBillingPeriod: Dispatch<BillingPeriod>;
  accountId: string;
  eodoService: EodoServiceClient;
  interestService: InterestServiceClient;
  ledgerService: LedgerServiceClient;
  regularPaymentService: RegularPaymentServiceClient;
  customerInformationService: CustomerInformationSystemClient;
  transactionService: TransactionServiceClient;
  selectedBillingPeriod: BillingPeriod | undefined;
  setSelectedBillingPeriod: Dispatch<SetStateAction<BillingPeriod>>;
  feeService: FeeServiceClient;
  creditAllocationService: CreditAllocationServiceClient;
  fakeAccessPayClient: FakeAccessPayClient;
  searchService: SearchServiceClient;
  annualStatementId: number | undefined;
  setAnnualStatementId: Dispatch<number | undefined>;
  selectedNosia: GetNosiasForAccount | undefined;
  setSelectedNosia: Dispatch<GetNosiasForAccount | undefined>;
  nosiaJsonView: boolean;
  setNosiaJsonView: Dispatch<boolean>;
}

const AccountContext = React.createContext<IAccountContext | undefined>(undefined);
function AccountContextProvider({ children }: PropsWithChildren) {
  const { tenantId } = useGlobalContext();
  const { billingDate, setBillingDate, accountId } = useAccountIdContext();
  const [eodDate, setEodDate] = useState(undefined as Date);
  const [error, setError] = useState(undefined as string);
  const [isRunningEod, setIsRunningEod] = useState(false);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<BillingPeriod>();
  const [previousBillingPeriod, setPreviousBillingPeriod] = useState<BillingPeriod>();
  const eodoService = useEodoService(billingDate, tenantId);
  const interestService = useInterestService(billingDate, tenantId);
  const ledgerService = useLedgerService(billingDate, tenantId);
  const transactionService = useTransactionService(billingDate, tenantId);
  const regularPaymentService = useRegularPaymentService(billingDate, tenantId);
  const customerInformationService = useCustomerInformationSystemService(tenantId);
  const feeService = useFeeService(billingDate, tenantId);
  const creditAllocationService = useCreditAllocationService(billingDate, tenantId);
  const { data: accessPayConfig } = useAccessPayConfiguration();
  const [accessPayBaseUrl, setAccessPayBaseUrl] = useState<string | undefined>()
  const fakeAccessPayClient = useFakeAccessPayClient(billingDate, tenantId);
  const searchService = useSearchService(tenantId);
  const [annualStatementId, setAnnualStatementId] = useState<number|undefined>();
  const [selectedNosia, setSelectedNosia] = useState<GetNosiasForAccount|undefined>();
  const [nosiaJsonView, setNosiaJsonView] = useState<boolean>(false);

    useEffect(() => {
        setAccessPayBaseUrl(accessPayConfig?.baseUrl);
    }, [accessPayConfig]);

    useEffect(() => {
        fakeAccessPayClient.accessPayBaseUrl = accessPayBaseUrl;
    }, [accessPayBaseUrl]);

    return (
    <AccountContext.Provider
      value={{
        error,
        setError,
        billingDate,
        setBillingDate,
        eodDate,
        setEodDate,
        isRunningEod,
        setIsRunningEod,
        previousBillingPeriod,
        setPreviousBillingPeriod,
        selectedBillingPeriod,
        setSelectedBillingPeriod,
        accountId,
        regularPaymentService,
        customerInformationService,
        eodoService,
        interestService,
        ledgerService,
        transactionService,
        feeService,
        creditAllocationService,
        searchService,
        fakeAccessPayClient,
        annualStatementId,
        setAnnualStatementId,
        selectedNosia,
        setSelectedNosia,
        nosiaJsonView,
        setNosiaJsonView,
      }}>
      {children}
    </AccountContext.Provider>
  );
}

export { AccountContextProvider, AccountContext };

export function useAccountContext() {
  return React.useContext(AccountContext);
}
